import React, { } from 'react'
import { Divider, Statistic } from 'antd'
import { BackButton, ChartWrapper, ChartWrapperInner, RowWrapper, StatisticWrapper, Title, VerticalLine, VerticalLineWrapper, WhiteWrapper } from '../invoice.styled'
import { useTranslation } from 'react-i18next'
import { Bar, Doughnut, HorizontalBar, Line } from 'react-chartjs-2'
import Taiwan from '../../DashboardSetting/charts/map/Taiwan'
import { ageChartProps, channelChartProps, channelShareChartProps, genderChartProps, trendChartProps } from './chartConf'
import dayjs from 'dayjs'
import { ArrowLeftOutlined, InboxOutlined } from '@ant-design/icons'
import { useLocation, useNavigate } from 'react-router-dom'

const mapRgbNumber = '106,159,58'
function InvoiceDashboard (props) {
  const { goBackPath } = props
  const { t: ln_t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { data } = location?.state || {}
  const { name, startDate, endDate, dashboardDatas } = data || {}
  const { summary_custom_date, gender_custom_date, age_custom_date, city_custom_date, summary_last_month, gender_last_month, age_last_month, city_last_month, trend_daily, channel_daily, channel_share } = dashboardDatas || {}
  const customDate = `${dayjs(startDate)?.format('YYYY/MM/DD')} - ${dayjs(endDate)?.format('YYYY/MM/DD')}`

  const statistics = [
    { key: 'inv_num', title: ln_t('invoice.quantity'), suffix: ln_t('invoice.quantityUnit') },
    { key: 'count_all', title: ln_t('invoice.transactions'), suffix: ln_t('invoice.transactionUnit') },
    { key: 'amount', title: ln_t('invoice.totalConsumers') },
    { key: 'uuid', title: ln_t('invoice.totalConsumption') }
  ]

  const getOpacity = i => i >= 15 ? 0.2 : i >= 10 ? 0.4 : i >= 5 ? 0.7 : 1
  const mapColorLastMonth = {}
  Object.keys(city_last_month?.data || {}).forEach((i, idx) => { mapColorLastMonth[i] = mapRgbNumber ? `rgba(${mapRgbNumber}, ${getOpacity(idx)})` : 'rgba(0,0,0,0.2)' })
  const mapColorCustom = {}
  Object.keys(city_custom_date?.data || {}).forEach((i, idx) => { mapColorCustom[i] = mapRgbNumber ? `rgba(${mapRgbNumber}, ${getOpacity(idx)})` : 'rgba(0,0,0,0.2)' })

  const chartTypes = {
    bar: props => <Bar {...props} />,
    horizontalBar: props => <HorizontalBar {...props} />,
    map: props => <Taiwan {...props} />,
    line: props => <Line {...props} />,
    doughnut: props => <Doughnut {...props} />
  }
  const chartComponent = (props) => {
    const { title, data, type, propsFunc, wrapperProps = {}, innerProps = {}, useDivider } = props || {}
    return <ChartWrapper {...wrapperProps}>
      {useDivider ? <Divider plain>{title}</Divider> : <Title>{title}</Title>}
      <ChartWrapperInner {...innerProps}>
        {data
          ? chartTypes[type]?.(propsFunc({ data, ln_t }))
          : <div style={{ padding: '60px 0' }}>
            <InboxOutlined style={{ color: 'rgba(0,0,0,.3)' }} /> {ln_t('dashboard.noData')}
          </div>}
      </ChartWrapperInner>
    </ChartWrapper>
  }

  return <>
    <BackButton>
      <ArrowLeftOutlined onClick={() => navigate(goBackPath)} /> {name}
    </BackButton>

    <WhiteWrapper>
      {/* ----- Consumption Custom Date -----  */}
      <Title>{customDate} {ln_t('invoice.consumptionOverview')}</Title>
      <StatisticWrapper>
        {statistics.map(({ key, ...i }, idx) => <React.Fragment key={key}>
          <Statistic value={summary_custom_date?.data?.[key]} {...i} />
          {idx !== statistics?.length - 1 && idx !== 1 && <VerticalLineWrapper><VerticalLine /></VerticalLineWrapper>}
        </React.Fragment>)}
      </StatisticWrapper>

      <RowWrapper>
        <div>
          {chartComponent({ title: ln_t('invoice.consumer'), data: gender_custom_date?.data, type: 'bar', propsFunc: genderChartProps, useDivider: true })}
          {chartComponent({
            title: ln_t('invoice.consumerAge'),
            data: age_custom_date?.data,
            type: 'horizontalBar',
            propsFunc: ageChartProps,
            wrapperProps: { style: { marginTop: '24px' } },
            useDivider: true
          })}
        </div>
        {chartComponent({
          title: ln_t('invoice.consumptionArea'),
          data: city_custom_date?.data,
          type: 'map',
          propsFunc: () => ({
            idKey: 'cityLastMonth',
            statistics: Object.entries(city_custom_date?.data)?.reduce((all, [key, value]) => ({ ...all, [key]: value.percentage }), {}),
            valueObj: Object.entries(city_custom_date?.data)?.reduce((all, [key, value]) => ({ ...all, [key]: value.count }), {}),
            areaColorSetting: mapColorLastMonth,
            height: '620px',
            afterFix: '%'
          }),
          wrapperProps: { fullheight: true },
          useDivider: true
        })}
      </RowWrapper>

      {/* ----- Consumer Last Month -----  */}
      <Title>
        {ln_t('invoice.recentConsumption')} <br />
        <span style={{ fontSize: '16px', fontWeight: 400 }}>{ln_t('invoice.recentConsumptionDesc')}</span>
      </Title>
      <StatisticWrapper>
        {statistics.map(({ key, ...i }, idx) => <React.Fragment key={key}>
          <Statistic value={summary_last_month?.data?.[key]} {...i} />
          {idx !== statistics?.length - 1 && idx !== 1 && <VerticalLineWrapper><VerticalLine /></VerticalLineWrapper>}
        </React.Fragment>)}
      </StatisticWrapper>

      <RowWrapper>
        <div>
          {chartComponent({ title: ln_t('invoice.consumer'), data: gender_last_month?.data, type: 'bar', propsFunc: genderChartProps, useDivider: true })}
          {chartComponent({
            title: ln_t('invoice.consumerAge'),
            data: age_last_month?.data,
            type: 'horizontalBar',
            propsFunc: ageChartProps,
            wrapperProps: { style: { marginTop: '24px' } },
            useDivider: true
          })}
        </div>
        {chartComponent({
          title: ln_t('invoice.consumptionArea'),
          data: city_last_month?.data,
          type: 'map',
          propsFunc: () => ({
            idKey: 'cityCustom',
            statistics: Object.entries(city_last_month?.data)?.reduce((all, [key, value]) => ({ ...all, [key]: value.percentage }), {}),
            valueObj: Object.entries(city_last_month?.data)?.reduce((all, [key, value]) => ({ ...all, [key]: value.count }), {}),
            areaColorSetting: mapColorCustom,
            height: '620px',
            afterFix: '%'
          }),
          wrapperProps: { fullheight: true },
          useDivider: true
        })}
      </RowWrapper>

      {/* ----- Other Charts -----  */}
      {chartComponent({
        title: ln_t('invoice.consumptionAmountTrend'),
        data: trend_daily?.data,
        type: 'bar',
        propsFunc: trendChartProps,
        wrapperProps: { fullwidth: true },
        innerProps: { borderless: true }
      })}

      {chartComponent({
        title: ln_t('invoice.salesTrendByChannel'),
        data: channel_daily?.data,
        type: 'line',
        propsFunc: channelChartProps,
        wrapperProps: { fullwidth: true },
        innerProps: { borderless: true }
      })}

      {chartComponent({
        title: ln_t('invoice.channelShareRanking'),
        data: channel_share?.data,
        type: 'doughnut',
        propsFunc: channelShareChartProps,
        wrapperProps: { fullwidth: true },
        innerProps: { borderless: true }
      })}
    </WhiteWrapper>
  </>
}

export default InvoiceDashboard
